import { getTerm, initializeApollo, getGraphQLData } from '@zillow/cms-blocks';
import { STICKY_SIDEBAR_TOP_PX } from '@zillow/zrs-cms';
import { GetStaticPaths, GetStaticProps } from 'next';
import React from 'react';

import type { PostsTemplateProps } from 'src/components/PostsTemplate/PostsTemplate.types';
import type { Block } from 'src/types/blocks';
import type { ResolvedBanner } from 'src/types/shared';

import { REVALIDATION_INTERVAL } from '../../server/config';
import { TMenuItem } from '../components/Menu/Menu.types';

import { processBlocksData, processResolvedBanner } from './blocks';
import { shouldUseVisualRefreshTheme } from './document';
import { withD } from './withDynamicThing';

// Define types for the component props and inputs
export type TermPageContentProps = {
    term: {
        id: string;
        link: string;
        description?: string;
        name: string;
        menu?: unknown;
        posts: PostsTemplateProps['posts'];
        sidebar?: { blocks: Block[] };
        footer?: { blocks: Block[] };
        banner?: ResolvedBanner | null;
        head: string;
        [key: string]: unknown;
    };
    initialPage: number;
    preview?: boolean;
    /** Site identifier for clickstream analytics */
    siteSlug?: string;
};

// Define the minimal props for LayoutComponent
export type LayoutComponentProps = {
    children: React.ReactNode;
    head: string;
    menu?: unknown;
    preview?: boolean;
    submenu?: unknown;
    isBlog?: boolean;
    [key: string]: unknown;
};

type BuildPrimaryTermPageInputs = {
    site:
        | 'ZPA'
        | 'LEARN'
        | 'ZHL'
        | 'RENTALS'
        | 'ROOT'
        | 'CAREERS'
        | 'CONSTRUCTION'
        | 'AGENTS'
        | 'PREFERRED'
        | 'NEWS'
        | 'RESEARCH';
    taxonomy: 'CATEGORY' | 'POST_TAG';
    // Component to render the layout
    LayoutComponent: React.ComponentType<LayoutComponentProps>;
    // Content component to render term pages
    ContentComponent: React.ComponentType<{
        term: TermPageContentProps['term'];
        initialPage: number;
        preview?: boolean;
        stickySidebarTop?: string;
    }>;
    // For special handling of page paths
    pathFormatter?: (slug: string[]) => string;
    // For handling additional layout props
    additionalLayoutProps?: Record<string, unknown>;
    // Skip applying withD to the Page component
    skipWithD?: boolean;
    // Posts per page
    postsPerPage?: string;
    // Specify which prop name to use for menu data ('menu' | 'submenu')
    menuPropName?: 'menu' | 'submenu';
    // Add basePath for consistent path construction
    basePath?: string;
    // Whether the page has a sticky nav
    hasStickyNav?: boolean;
};

export async function buildPrimaryTermPage(
    inputs: BuildPrimaryTermPageInputs
): Promise<{
    getStaticProps: GetStaticProps<TermPageContentProps>;
    getStaticPaths: GetStaticPaths;
    Page: React.ComponentType<TermPageContentProps>;
    WrappedPage: React.ComponentType<TermPageContentProps>;
}> {
    const getStaticProps: GetStaticProps<TermPageContentProps> = async ({
        params,
    }) => {
        const { slug } = params || {};
        // Ensure slug is an array of strings
        const slugArray = Array.isArray(slug)
            ? slug.map(String)
            : [String(slug)];

        const hasPageNumber =
            slugArray.length >= 3 && slugArray[slugArray.length - 2] === 'page';
        const pageNumber = hasPageNumber
            ? Number(slugArray[slugArray.length - 1])
            : 1;
        const termSlug = hasPageNumber
            ? slugArray[slugArray.length - 3] || ''
            : slugArray[slugArray.length - 1] || '';

        initializeApollo();
        let term, settings;
        try {
            const result = await getTerm({
                variables: {
                    site: inputs.site,
                    id: termSlug,
                    idType: 'SLUG',
                    taxonomy: inputs.taxonomy,
                    termPostsType: 'POST',
                    termPostsPage: pageNumber.toString(),
                    termPostsPerPage: inputs.postsPerPage || '12',
                    withSettings: true,
                },
                fetchPolicy: 'no-cache',
            });
            term = result.term;
            settings = result.settings;
        } catch (error) {
            if (
                error?.message ===
                getGraphQLData.ErrorCodes.ZG_GRAPH_PAGINATION_ERROR
            ) {
                return {
                    notFound: true,
                };
            }

            throw error;
        }

        if (!term) {
            return {
                notFound: true,
            };
        }

        const footerBlocks = await processBlocksData(term?.footer);
        const sidebarBlocks = await processBlocksData(term?.sidebar);
        const banner = await processResolvedBanner(term?.banner);

        // Determine the path for visual refresh theme
        let visualRefreshPath: string;
        if (inputs.pathFormatter) {
            visualRefreshPath = inputs.pathFormatter(slugArray);
        } else if (inputs.basePath) {
            // Ensure basePath has no trailing slash
            const sanitizedBasePath = inputs.basePath.replace(/\/$/, '');
            visualRefreshPath = `${sanitizedBasePath}/${termSlug}/`;
        } else {
            visualRefreshPath = `/${inputs.taxonomy.toLowerCase()}/${termSlug}/`;
        }
        const useVisualRefreshTheme =
            shouldUseVisualRefreshTheme(visualRefreshPath);

        const props: TermPageContentProps & {
            useVisualRefreshTheme?: boolean;
        } = {
            term: {
                ...term,
                menu: settings?.menu || null,
                footer:
                    footerBlocks.length > 0
                        ? { ...term.footer, blocks: footerBlocks }
                        : null,
                sidebar:
                    sidebarBlocks.length > 0
                        ? { ...term.sidebar, blocks: sidebarBlocks }
                        : null,
                banner,
            },
            initialPage: pageNumber,
            useVisualRefreshTheme,
            siteSlug: inputs.site,
        };

        return {
            props,
            revalidate: REVALIDATION_INTERVAL,
        };
    };

    const getStaticPaths: GetStaticPaths = async () => {
        return {
            paths: [],
            fallback: 'blocking',
        };
    };

    const PageComponent: React.FC<
        TermPageContentProps & { useVisualRefreshTheme?: boolean }
    > = ({ term, initialPage, preview, useVisualRefreshTheme }) => {
        // Determine layout props based on site and component
        const layoutProps: LayoutComponentProps = {
            head: term.head,
            preview,
            isBlog: true,
            ...inputs.additionalLayoutProps,
            children: (
                <inputs.ContentComponent
                    term={term}
                    initialPage={initialPage}
                    preview={preview}
                    stickySidebarTop={
                        inputs.hasStickyNav
                            ? `${STICKY_SIDEBAR_TOP_PX}px`
                            : undefined
                    }
                />
            ),
        };

        // Add menu props based on specified prop name
        if (term.menu) {
            const propName = inputs.menuPropName || 'menu';
            layoutProps[propName] = term.menu as { items: TMenuItem[] };
        }
        // Optionally add useVisualRefreshTheme to layoutProps if needed by LayoutComponent
        if (typeof useVisualRefreshTheme !== 'undefined') {
            layoutProps.useVisualRefreshTheme = useVisualRefreshTheme;
        }

        return <inputs.LayoutComponent {...layoutProps} />;
    };

    // Apply withD to the Page component, unless skipWithD is specified
    const WrappedPage = inputs.skipWithD
        ? PageComponent
        : await withD(PageComponent);

    return { getStaticProps, getStaticPaths, Page: PageComponent, WrappedPage };
}
